"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.resolvedEventBookingType = resolvedEventBookingType;
exports.normalizeHm = normalizeHm;
exports.dateMatchesEventSchedule = dateMatchesEventSchedule;
exports.getNextOccurrenceDate = getNextOccurrenceDate;
exports.getEventCardDateLabel = getEventCardDateLabel;
exports.isEventVisibleForCard = isEventVisibleForCard;
exports.isEventVisibleForResultsCard = isEventVisibleForResultsCard;
exports.getEventCardGuestLabel = getEventCardGuestLabel;
exports.getFilterPageDateParam = getFilterPageDateParam;
const TDR_1 = require("../types/TDR");
/** JS getDay(): 0 Sun … 6 Sat — values align with ordering used in occurrence helpers below. */
const JS_DAY_TO_KEY = [
    TDR_1.TDR.Weekday.Sun,
    TDR_1.TDR.Weekday.Mon,
    TDR_1.TDR.Weekday.Tues,
    TDR_1.TDR.Weekday.Wed,
    TDR_1.TDR.Weekday.Thurs,
    TDR_1.TDR.Weekday.Fri,
    TDR_1.TDR.Weekday.Sat
];
function isoPartsFromYYYYMMDD(dateStr) {
    const m = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m)
        return null;
    return { y: parseInt(m[1], 10), m: parseInt(m[2], 10), d: parseInt(m[3], 10) };
}
/** Normalize admin `MMMM dd, yyyy` to `YYYY-MM-DD` using UTC calendar parts. */
function isoFromFormattedConditionDate(conditionDate) {
    const parsed = Date.parse(conditionDate);
    if (Number.isNaN(parsed))
        return null;
    const dt = new Date(parsed);
    const y = dt.getUTCFullYear();
    const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(dt.getUTCDate()).padStart(2, '0');
    return `${y}-${mm}-${dd}`;
}
function resolvedEventBookingType(event) {
    return event.eventType === 'table' ? 'table' : 'param';
}
/** `HH:mm` for calendar / URL; undefined if missing or invalid. */
function normalizeHm(s) {
    if (s === null || !String(s).trim()) {
        return undefined;
    }
    const m = String(s).trim().match(/^(\d{1,2}):(\d{2})/);
    if (!m) {
        return undefined;
    }
    const h = Math.min(23, Math.max(0, parseInt(m[1], 10)));
    const min = Math.min(59, Math.max(0, parseInt(m[2], 10)));
    return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}
/** Whether `YYYY-MM-DD` falls on one of this event's allowed calendar dates (given schedule + condition). */
function dateMatchesEventSchedule(dateStr, event) {
    var _a, _b;
    const parts = isoPartsFromYYYYMMDD(dateStr);
    if (!parts)
        return false;
    const sched = (_a = event.eventSchedule) !== null && _a !== void 0 ? _a : 'non-recurring';
    const v = (_b = event.condition) === null || _b === void 0 ? void 0 : _b.value;
    if (sched === 'permanent')
        return true;
    if (sched === 'non-recurring') {
        const dates = v === null || v === void 0 ? void 0 : v.dates;
        if (dates === null || dates === void 0 ? void 0 : dates.length) {
            const allowed = new Set(dates.map(isoFromFormattedConditionDate).filter((x) => x != null));
            return allowed.has(dateStr);
        }
        const between = v === null || v === void 0 ? void 0 : v.betweenDates;
        if ((between === null || between === void 0 ? void 0 : between.length) === 2) {
            const startMs = Date.parse(between[0]);
            const endMs = Date.parse(between[1]);
            if (Number.isNaN(startMs) || Number.isNaN(endMs))
                return false;
            const cand = new Date(parts.y, parts.m - 1, parts.d);
            cand.setHours(0, 0, 0, 0);
            const start = new Date(startMs);
            start.setHours(0, 0, 0, 0);
            const end = new Date(endMs);
            end.setHours(0, 0, 0, 0);
            return cand >= start && cand <= end;
        }
        return false;
    }
    if (sched === 'recurring') {
        const daysOfWeek = v === null || v === void 0 ? void 0 : v.daysOfWeek;
        if (daysOfWeek === null || daysOfWeek === void 0 ? void 0 : daysOfWeek.length) {
            const cand = new Date(parts.y, parts.m - 1, parts.d);
            const key = JS_DAY_TO_KEY[cand.getDay()];
            return daysOfWeek.includes(key);
        }
        const dom = v === null || v === void 0 ? void 0 : v.daysOfMonth;
        if (dom === null || dom === void 0 ? void 0 : dom.length)
            return dom.includes(parts.d);
        const yearDates = v === null || v === void 0 ? void 0 : v.dates;
        if (yearDates === null || yearDates === void 0 ? void 0 : yearDates.length) {
            for (const ds of yearDates) {
                const parsed = Date.parse(ds);
                if (Number.isNaN(parsed))
                    continue;
                const b = new Date(parsed);
                if (b.getMonth() + 1 === parts.m && b.getDate() === parts.d)
                    return true;
            }
            return false;
        }
    }
    return false;
}
function eventScheduleOf(event) {
    var _a;
    return (_a = event.eventSchedule) !== null && _a !== void 0 ? _a : 'non-recurring';
}
function toLocalMidnight(d) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
function formatEventCardDate(d) {
    return toLocalMidnight(d).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
}
function dedupeDatesByCalendarDay(dates) {
    const seen = new Set();
    const out = [];
    for (const d of dates) {
        const x = toLocalMidnight(d);
        const key = `${x.getFullYear()}-${x.getMonth()}-${x.getDate()}`;
        if (seen.has(key))
            continue;
        seen.add(key);
        out.push(x);
    }
    return out.sort((a, b) => a.getTime() - b.getTime());
}
function getNonRecurringEventCardDateLabel(event) {
    var _a, _b, _c, _d;
    const dates = (_b = (_a = event.condition) === null || _a === void 0 ? void 0 : _a.value) === null || _b === void 0 ? void 0 : _b.dates;
    if (dates === null || dates === void 0 ? void 0 : dates.length) {
        const parsed = dates
            .map((d) => new Date(d))
            .filter((d) => !Number.isNaN(d.getTime()));
        if (!parsed.length)
            return null;
        const uniqueSorted = dedupeDatesByCalendarDay(parsed);
        if (!uniqueSorted.length)
            return null;
        if (uniqueSorted.length === 1) {
            return formatEventCardDate(uniqueSorted[0]);
        }
        return 'Select days';
    }
    const between = (_d = (_c = event.condition) === null || _c === void 0 ? void 0 : _c.value) === null || _d === void 0 ? void 0 : _d.betweenDates;
    if ((between === null || between === void 0 ? void 0 : between.length) === 2) {
        const start = new Date(between[0]);
        const end = new Date(between[1]);
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()))
            return null;
        const s = toLocalMidnight(start);
        const e = toLocalMidnight(end);
        if (s.getTime() === e.getTime()) {
            return formatEventCardDate(s);
        }
        return 'Select days';
    }
    return null;
}
function getNextYearlyOccurrenceFromDates(dates, from) {
    let best = null;
    for (const dateStr of dates) {
        const parsed = Date.parse(dateStr);
        if (Number.isNaN(parsed))
            continue;
        const base = new Date(parsed);
        let candidate = new Date(from.getFullYear(), base.getMonth(), base.getDate());
        candidate.setHours(0, 0, 0, 0);
        const fromMid = new Date(from);
        fromMid.setHours(0, 0, 0, 0);
        if (candidate < fromMid) {
            candidate = new Date(from.getFullYear() + 1, base.getMonth(), base.getDate());
            candidate.setHours(0, 0, 0, 0);
        }
        if (best === null || candidate < best)
            best = candidate;
    }
    return best;
}
function getNextMonthlyOccurrence(daysOfMonth, from) {
    const sorted = [...new Set(daysOfMonth)]
        .filter((d) => d >= 1 && d <= 31)
        .sort((a, b) => a - b);
    if (!sorted.length)
        return null;
    const fromDay = new Date(from);
    fromDay.setHours(0, 0, 0, 0);
    for (let mAdd = 0; mAdd <= 14; mAdd++) {
        const y = fromDay.getFullYear();
        const m = fromDay.getMonth() + mAdd;
        const monthStart = new Date(y, m, 1);
        const daysInMonth = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0).getDate();
        for (const dom of sorted) {
            if (dom > daysInMonth)
                continue;
            const candidate = new Date(monthStart.getFullYear(), monthStart.getMonth(), dom);
            candidate.setHours(0, 0, 0, 0);
            if (candidate >= fromDay) {
                return candidate;
            }
        }
    }
    return null;
}
/**
 * ISO date YYYY-MM-DD for the next occurrence of a recurring event; null otherwise.
 */
function getNextOccurrenceDate(event) {
    var _a, _b, _c, _d, _e, _f;
    if (eventScheduleOf(event) !== 'recurring')
        return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const daysOfWeek = (_b = (_a = event.condition) === null || _a === void 0 ? void 0 : _a.value) === null || _b === void 0 ? void 0 : _b.daysOfWeek;
    if (daysOfWeek === null || daysOfWeek === void 0 ? void 0 : daysOfWeek.length) {
        const tdrOrder = [
            TDR_1.TDR.Weekday.Sun,
            TDR_1.TDR.Weekday.Mon,
            TDR_1.TDR.Weekday.Tues,
            TDR_1.TDR.Weekday.Wed,
            TDR_1.TDR.Weekday.Thurs,
            TDR_1.TDR.Weekday.Fri,
            TDR_1.TDR.Weekday.Sat,
        ];
        for (let i = 0; i <= 7; i++) {
            const d = new Date(today);
            d.setDate(today.getDate() + i);
            const key = tdrOrder[d.getDay()];
            if (daysOfWeek.includes(key)) {
                const y = d.getFullYear();
                const m = String(d.getMonth() + 1).padStart(2, '0');
                const day = String(d.getDate()).padStart(2, '0');
                return `${y}-${m}-${day}`;
            }
        }
    }
    const daysOfMonth = (_d = (_c = event.condition) === null || _c === void 0 ? void 0 : _c.value) === null || _d === void 0 ? void 0 : _d.daysOfMonth;
    if (daysOfMonth === null || daysOfMonth === void 0 ? void 0 : daysOfMonth.length) {
        const next = getNextMonthlyOccurrence(daysOfMonth, today);
        if (!next)
            return null;
        const y = next.getFullYear();
        const m = String(next.getMonth() + 1).padStart(2, '0');
        const day = String(next.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    }
    const dates = (_f = (_e = event.condition) === null || _e === void 0 ? void 0 : _e.value) === null || _f === void 0 ? void 0 : _f.dates;
    if (dates === null || dates === void 0 ? void 0 : dates.length) {
        const candidate = getNextYearlyOccurrenceFromDates(dates, today);
        if (!candidate)
            return null;
        const y = candidate.getFullYear();
        const m = String(candidate.getMonth() + 1).padStart(2, '0');
        const day = String(candidate.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    }
    return null;
}
/**
 * Human-readable schedule label for the event card.
 */
function getEventCardDateLabel(event) {
    var _a, _b, _c, _d;
    if (eventScheduleOf(event) === 'permanent')
        return null;
    if (eventScheduleOf(event) === 'recurring') {
        const daysOfWeek = (_b = (_a = event.condition) === null || _a === void 0 ? void 0 : _a.value) === null || _b === void 0 ? void 0 : _b.daysOfWeek;
        if (daysOfWeek === null || daysOfWeek === void 0 ? void 0 : daysOfWeek.length) {
            const fullNames = {
                mon: 'Monday',
                tues: 'Tuesday',
                wed: 'Wednesday',
                thurs: 'Thursday',
                fri: 'Friday',
                sat: 'Saturday',
                sun: 'Sunday'
            };
            if (daysOfWeek.length === 1)
                return fullNames[daysOfWeek[0]];
            return `${daysOfWeek.map((d) => fullNames[d]).join(', ')}`;
        }
        const dom = (_d = (_c = event.condition) === null || _c === void 0 ? void 0 : _c.value) === null || _d === void 0 ? void 0 : _d.daysOfMonth;
        if (dom === null || dom === void 0 ? void 0 : dom.length) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const next = getNextMonthlyOccurrence(dom, today);
            if (next) {
                return next.toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                });
            }
        }
        const isoDate = getNextOccurrenceDate(event);
        if (isoDate) {
            return new Date(`${isoDate}T12:00:00`).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
            });
        }
    }
    if (eventScheduleOf(event) === 'non-recurring') {
        return getNonRecurringEventCardDateLabel(event);
    }
    return null;
}
/** Whether an event should appear on filter/results event cards (upcoming schedule only). */
function isEventVisibleForCard(event) {
    var _a, _b, _c, _d, _e;
    const sched = (_a = event.eventSchedule) !== null && _a !== void 0 ? _a : 'non-recurring';
    if (sched === 'permanent' || sched === 'recurring') {
        return true;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dates = (_c = (_b = event.condition) === null || _b === void 0 ? void 0 : _b.value) === null || _c === void 0 ? void 0 : _c.dates;
    if (dates === null || dates === void 0 ? void 0 : dates.length) {
        return dates.some((d) => {
            const iso = isoFromFormattedConditionDate(d);
            if (!iso)
                return false;
            const parts = isoPartsFromYYYYMMDD(iso);
            if (!parts)
                return false;
            const dt = new Date(parts.y, parts.m - 1, parts.d);
            dt.setHours(0, 0, 0, 0);
            return dt > today;
        });
    }
    const betweenDates = (_e = (_d = event.condition) === null || _d === void 0 ? void 0 : _d.value) === null || _e === void 0 ? void 0 : _e.betweenDates;
    if ((betweenDates === null || betweenDates === void 0 ? void 0 : betweenDates.length) === 2) {
        const endMs = Date.parse(betweenDates[1]);
        if (Number.isNaN(endMs))
            return true;
        const end = new Date(endMs);
        end.setHours(0, 0, 0, 0);
        return end >= today;
    }
    return true;
}
/** Inventory / results cards: param events only (table-type events stay on the filter page). */
function isEventVisibleForResultsCard(event) {
    return isEventVisibleForCard(event) && event.eventType !== 'table';
}
/** Human-readable guest requirement for the event card (min/max party size). */
function getEventCardGuestLabel(event) {
    var _a, _b, _c, _d;
    const min = (_b = (_a = event.condition) === null || _a === void 0 ? void 0 : _a.value) === null || _b === void 0 ? void 0 : _b.minGuestCount;
    const max = (_d = (_c = event.condition) === null || _c === void 0 ? void 0 : _c.value) === null || _d === void 0 ? void 0 : _d.maxGuestCount;
    if (min == null && max == null) {
        return null;
    }
    if (min != null && max != null) {
        if (min === max) {
            return `${min}`;
        }
        return `${min}-${max}`;
    }
    if (min != null) {
        return `${min}+`;
    }
    return `Up to ${max}`;
}
/**
 * ISO date (YYYY-MM-DD) for filter-page pre-fill: recurring (next occurrence),
 * non-recurring (first upcoming date), null otherwise.
 */
function getFilterPageDateParam(event) {
    var _a, _b, _c, _d, _e;
    const sched = (_a = event.eventSchedule) !== null && _a !== void 0 ? _a : 'non-recurring';
    if (sched === 'recurring') {
        return getNextOccurrenceDate(event);
    }
    if (sched === 'non-recurring') {
        const dates = (_c = (_b = event.condition) === null || _b === void 0 ? void 0 : _b.value) === null || _c === void 0 ? void 0 : _c.dates;
        if (dates === null || dates === void 0 ? void 0 : dates.length) {
            const upcoming = dates
                .map((d) => new Date(d))
                .filter((d) => d > new Date())
                .sort((a, b) => a.getTime() - b.getTime());
            if (upcoming.length) {
                const d = upcoming[0];
                const y = d.getFullYear();
                const m = String(d.getMonth() + 1).padStart(2, '0');
                const day = String(d.getDate()).padStart(2, '0');
                return `${y}-${m}-${day}`;
            }
        }
        const between = (_e = (_d = event.condition) === null || _d === void 0 ? void 0 : _d.value) === null || _e === void 0 ? void 0 : _e.betweenDates;
        if ((between === null || between === void 0 ? void 0 : between.length) === 2) {
            const parsed = Date.parse(between[0]);
            if (!Number.isNaN(parsed)) {
                const d = new Date(parsed);
                const y = d.getUTCFullYear();
                const m = String(d.getUTCMonth() + 1).padStart(2, '0');
                const day = String(d.getUTCDate()).padStart(2, '0');
                return `${y}-${m}-${day}`;
            }
        }
    }
    return null;
}
//# sourceMappingURL=eventBooking.js.map